import { AfterViewInit, ChangeDetectionStrategy, Component, Directive, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { DatetimeCustomEvent, IonDatetime, PopoverController } from '@ionic/angular/standalone';

export type DateChangerType = 'date' | 'date-time' | 'time';

@Directive({
	selector: '[dateChanger]',
	standalone: true
})
export class DateChangerDirective {

	@Input('dateChanger') dateValue: string | null | undefined = undefined;
	@Input() dateFormat: DateChangerType = 'date';
	@Input() hourValues: string | number[] | number | undefined = undefined;
	@Input() minuteValues: string | number[] | number | undefined = undefined;
	@Input() minDate: string | undefined = undefined;
	@Input() maxDate: string | undefined = undefined;
	@Input() showClearButton: boolean = false;
	@Input() dateSelectionDisabled: boolean = false;
	@Input() reference: 'trigger' | 'event' = 'trigger';
	@Input() side: 'top' | 'right' | 'bottom' | 'left' | 'start' | 'end' = 'bottom';
	@Input() alignment: 'start' | 'center' | 'end' = 'center';
	@Output() dateChanged: EventEmitter<string | null> = new EventEmitter<string | null>(); // can return null if the date is cleared

	constructor(private popoverController: PopoverController) {
	}

	@HostListener('click', ['$event']) async onClick(event: Event) {
		if (this.dateSelectionDisabled) return;
		const popover = await this.popoverController.create({
			component: DateChangerPopover,
			event: event,
			reference: this.reference,
			alignment: this.alignment,
			side: this.side,
			showBackdrop: false,
			componentProps: {
				dateValue: this.dateValue,
				dateFormat: this.dateFormat,
				minDate: this.minDate,
				maxDate: this.maxDate,
				hourValues: this.hourValues,
				minuteValues: this.minuteValues,
				showClearButton: this.showClearButton
			}
		});
		await popover.present();
		const { data } = await popover.onWillDismiss();
		if (data) {
			const dateValue = data.dateValue;
			if (this.showClearButton && typeof dateValue === 'undefined') {
				this.dateChanged.emit(null);

			}
			else if (typeof dateValue === 'string' && dateValue && dateValue !== this.dateValue) {
				this.dateChanged.emit(data.dateValue);
			}
		}
	}
}

@Component({
	template: '<ion-datetime #datetime doneText="Ok" locale="en-GB" hourCycle="h23" (ionChange)="dateValueChanged($event)"></ion-datetime>',
	imports: [IonDatetime],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DateChangerPopover implements AfterViewInit {

	@Input() showClearButton: boolean = false;
	@Input() dateValue: string | undefined = undefined;
	@Input() dateFormat: DateChangerType = 'date';
	@Input() minDate: string | undefined = undefined;
	@Input() maxDate: string | undefined = undefined;
	@Input() hourValues: string | number[] | number | undefined = undefined;
	@Input() minuteValues: string | number[] | number | undefined = undefined;

	@ViewChild(IonDatetime) datetime!: IonDatetime;

	constructor(private popoverController: PopoverController) {
	}

	ngAfterViewInit(): void {
		// this seems to be the only way to initialise ion-datetime properties within an ion-popover. Does not work initialising in the template at time of writing (Ionic 8)
		setTimeout(() => {
			this.datetime.presentation = this.dateFormat;
			this.datetime.min = this.minDate;
			this.datetime.max = this.maxDate;
			this.datetime.hourValues = this.hourValues;
			this.datetime.minuteValues = this.minuteValues;
			this.datetime.showDefaultButtons = true;
			this.datetime.showClearButton = this.showClearButton;
			this.datetime.value = this.dateValue;
		});
	}

	dateValueChanged(event: Event) {
		this.popoverController.dismiss({ dateValue: (event as DatetimeCustomEvent).detail.value });
	}
}